/*
 * Назначение: страница «Диалоги».
 * Функционал:
 * - список диалогов текущего проекта с фильтрами, поиском и СЕРВЕРНОЙ
 *   пагинацией (бесконечная прокрутка: подгружаем по странице при докрутке
 *   до низа списка — диалоги не грузятся все сразу);
 * - карточка выбранного диалога с историей сообщений и мета-информацией;
 * - передача диалога живому менеджеру и возврат боту (handoff): пока диалог
 *   «у менеджера» — ИИ не отвечает; менеджер может писать клиенту прямо из
 *   карточки, сообщение уходит в его канал (Telegram/MAX сразу, web — при
 *   следующем обращении клиента);
 * - удаление одного диалога и массовое удаление по фильтру.
 * Все данные — из /api/admin/dialogs.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  AlertCircle,
  Bot,
  Headset,
  Loader2,
  MessageSquare,
  Paperclip,
  RotateCcw,
  Search,
  Send,
  Trash2,
  User,
  Wrench,
} from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { dialogsApi } from "../../lib/admin-api";
import { ApiError } from "../../lib/api";
import {
  CHANNEL_LABEL,
  DIALOG_STATUS_LABEL,
  formatDateTime,
  formatTime,
} from "../../lib/format";
import { useProject } from "../components/project/project-provider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";

const PAGE_SIZE = 30;

const STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  active: "default",
  handed_off: "outline",
  lead_captured: "secondary",
  closed: "secondary",
};

function initials(name: string | null | undefined, fallback: string): string {
  const base = (name || fallback).trim();
  if (!base) return "?";
  return base
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function formatBytes(size: number): string {
  if (!size || size < 1024) return `${size} Б`;
  const kb = size / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} КБ`;
  return `${(kb / 1024).toFixed(1)} МБ`;
}

export function DialogsPage() {
  const { currentSlug } = useProject();
  const params = useParams<{ dialogId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [confirmDelete, setConfirmDelete] = useState<
    | { kind: "single"; id: string; title: string }
    | { kind: "bulk"; count: number }
    | null
  >(null);
  const [replyText, setReplyText] = useState("");
  const scrollWrapRef = useRef<HTMLDivElement | null>(null);

  const selectedId = params.dialogId ?? null;
  const setSelectedId = (id: string | null) => {
    if (id) {
      navigate(`/dialogs/${id}`, { replace: false });
    } else {
      navigate("/dialogs", { replace: false });
    }
  };

  const listFilters = {
    search: search.trim() || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    channel: channelFilter !== "all" ? channelFilter : undefined,
  };

  // Серверная пагинация через бесконечную прокрутку.
  // Список авто-обновляем раз в 10 сек, чтобы новые диалоги/сообщения
  // появлялись без ручного «обновить страницу».
  const listQuery = useInfiniteQuery({
    queryKey: ["dialogs", currentSlug, { ...listFilters }],
    queryFn: ({ pageParam = 1 }) =>
      dialogsApi.list({ page: pageParam, page_size: PAGE_SIZE, ...listFilters }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.page_size;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    enabled: !!currentSlug,
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
  });

  const dialogs = useMemo(
    () => (listQuery.data?.pages ?? []).flatMap((p) => p.items),
    [listQuery.data],
  );
  const total = listQuery.data?.pages?.[0]?.total ?? 0;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dialogsApi.remove(id),
    onSuccess: (_void, id) => {
      toast.success("Диалог удалён");
      queryClient.invalidateQueries({ queryKey: ["dialogs", currentSlug] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      if (selectedId === id) navigate("/dialogs", { replace: true });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось удалить");
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: () =>
      dialogsApi.bulkRemove({
        channel: listFilters.channel,
        status: listFilters.status,
        search: listFilters.search,
        confirm: true,
      }),
    onSuccess: (res) => {
      toast.success(`Удалено диалогов: ${res.deleted}`);
      queryClient.invalidateQueries({ queryKey: ["dialogs", currentSlug] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      navigate("/dialogs", { replace: true });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось удалить");
    },
  });

  // Авто-выбор первого диалога только на первой странице
  useEffect(() => {
    if (!listQuery.data) return;
    if (dialogs.length === 0) {
      if (selectedId) navigate("/dialogs", { replace: true });
      return;
    }
    if (!selectedId) {
      navigate(`/dialogs/${dialogs[0].id}`, { replace: true });
    }
  }, [listQuery.data, dialogs, selectedId, navigate]);

  const detailsQuery = useQuery({
    queryKey: ["dialog", currentSlug, selectedId],
    queryFn: () => dialogsApi.get(selectedId as string),
    enabled: !!selectedId,
    // Подтягиваем новые сообщения клиента автоматически: каждые 5 сек, когда
    // диалог открыт (и активный, и handed_off, и lead_captured). На закрытом
    // обновлять смысла нет.
    refetchInterval: (q) =>
      q.state.data && q.state.data.status !== "closed" ? 5000 : false,
    refetchOnWindowFocus: true,
  });

  const handoffMutation = useMutation({
    mutationFn: (id: string) => dialogsApi.handoff(id),
    onSuccess: (data) => {
      toast.success("Диалог передан менеджеру — ИИ больше не отвечает");
      queryClient.setQueryData(["dialog", currentSlug, data.id], data);
      queryClient.invalidateQueries({ queryKey: ["dialogs", currentSlug] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось передать");
    },
  });

  const returnMutation = useMutation({
    mutationFn: (id: string) => dialogsApi.returnToBot(id),
    onSuccess: (data) => {
      toast.success("Диалог возвращён боту");
      queryClient.setQueryData(["dialog", currentSlug, data.id], data);
      queryClient.invalidateQueries({ queryKey: ["dialogs", currentSlug] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось вернуть боту");
    },
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      dialogsApi.reply(id, text),
    onSuccess: (data) => {
      setReplyText("");
      queryClient.setQueryData(["dialog", currentSlug, data.id], data);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось отправить");
    },
  });

  const managerFileInputRef = useRef<HTMLInputElement | null>(null);
  const managerFileMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      dialogsApi.uploadAttachment(id, file),
    onSuccess: (data) => {
      toast.success("Файл отправлен клиенту");
      queryClient.setQueryData(["dialog", currentSlug, data.id], data);
      queryClient.invalidateQueries({ queryKey: ["dialogs", currentSlug] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось отправить файл");
    },
  });

  const dialogsList = dialogs;
  const selected = detailsQuery.data;
  const isHandedOff = selected?.status === "handed_off";

  const headerInfo = useMemo(() => {
    if (!selected) return null;
    return {
      total: selected.messages.length,
      duration:
        selected.last_message_at && selected.started_at
          ? Math.max(
              0,
              Math.round(
                (new Date(selected.last_message_at).getTime() -
                  new Date(selected.started_at).getTime()) /
                  60000,
              ),
            )
          : 0,
    };
  }, [selected]);

  // Бесконечная прокрутка: подгружаем следующую страницу при докрутке вниз.
  const onListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 120 &&
      listQuery.hasNextPage &&
      !listQuery.isFetchingNextPage
    ) {
      listQuery.fetchNextPage();
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">Диалоги</h2>
        <p className="text-sm text-muted-foreground">История переписок с клиентами</p>
      </div>

      <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)] grid-rows-[minmax(0,1fr)]">
        {/* Список */}
        <Card className="col-span-4 flex flex-col min-h-0 overflow-hidden">
          <div className="p-4 space-y-4 border-b">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Всего: {total}
              </p>
              {(dialogsList.length > 0 ||
                listFilters.search ||
                listFilters.status ||
                listFilters.channel) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setConfirmDelete({ kind: "bulk", count: total })}
                  disabled={total === 0}
                  title="Удалить все диалоги, которые сейчас в фильтре"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Удалить
                </Button>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по имени, контакту, тексту..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="active">Активные</SelectItem>
                  <SelectItem value="handed_off">У менеджера</SelectItem>
                  <SelectItem value="lead_captured">С заявкой</SelectItem>
                  <SelectItem value="closed">Завершённые</SelectItem>
                </SelectContent>
              </Select>
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Канал" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все каналы</SelectItem>
                  <SelectItem value="web">Сайт</SelectItem>
                  <SelectItem value="telegram">Telegram</SelectItem>
                  <SelectItem value="max">MAX</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div
            ref={scrollWrapRef}
            onScroll={onListScroll}
            className="flex-1 min-h-0 overflow-y-auto"
          >
            <div className="p-2">
              {listQuery.isLoading && (
                <div className="text-sm text-muted-foreground p-4 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Загружаем…
                </div>
              )}
              {listQuery.isError && (
                <div className="text-sm text-destructive p-4 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Не удалось загрузить
                </div>
              )}
              {!listQuery.isLoading && dialogsList.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Пока нет диалогов. Они появятся, как только клиенты начнут
                  писать ИИ-продавцу.
                </div>
              )}
              {dialogsList.map((dialog) => (
                <button
                  key={dialog.id}
                  onClick={() => setSelectedId(dialog.id)}
                  className={`w-full p-3 rounded-lg text-left transition-colors mb-2 border ${
                    selectedId === dialog.id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border bg-card hover:bg-muted"
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {initials(dialog.user_name, dialog.visitor_id)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">
                          {dialog.user_name || `Гость ${dialog.visitor_id.slice(0, 6)}`}
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {CHANNEL_LABEL[dialog.channel]}
                          </Badge>
                          {dialog.status === "handed_off" && (
                            <Badge variant="secondary" className="text-xs h-4 px-1.5">
                              У менеджера
                            </Badge>
                          )}
                          {dialog.has_lead && (
                            <Badge variant="destructive" className="text-xs h-4 px-1.5">
                              Заявка
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs opacity-80">
                      {formatTime(dialog.last_message_at ?? dialog.started_at)}
                    </span>
                  </div>
                  <p className="text-sm truncate opacity-80 mt-1">
                    {dialog.last_message_preview || "Нет сообщений"}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-xs opacity-70">
                    <MessageSquare className="h-3 w-3" />
                    <span>{dialog.messages_count}</span>
                  </div>
                </button>
              ))}
              {listQuery.isFetchingNextPage && (
                <div className="text-xs text-muted-foreground p-3 flex items-center justify-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Подгружаем ещё…
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Карточка диалога */}
        <Card className="col-span-5 flex flex-col min-h-0 overflow-hidden">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Выбери диалог в списке слева</p>
              </div>
            </div>
          ) : detailsQuery.isLoading ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Загружаем диалог…
            </div>
          ) : !selected ? (
            <div className="flex-1 flex items-center justify-center text-destructive">
              <AlertCircle className="h-5 w-5 mr-2" />
              Не удалось загрузить
            </div>
          ) : (
            <>
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback>
                        {initials(selected.user_name, selected.visitor_id)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-medium">
                        {selected.user_name ||
                          `Гость ${selected.visitor_id.slice(0, 8)}`}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {CHANNEL_LABEL[selected.channel]}
                        </Badge>
                        <Badge
                          variant={STATUS_VARIANT[selected.status] ?? "secondary"}
                          className="text-xs"
                        >
                          {DIALOG_STATUS_LABEL[selected.status]}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right text-xs text-muted-foreground mr-1">
                      <p>
                        {headerInfo?.total} сообщений
                        {headerInfo && headerInfo.duration > 0
                          ? `, ${headerInfo.duration} мин`
                          : ""}
                      </p>
                      <p className="font-mono">{selected.id.slice(0, 8)}</p>
                    </div>
                    {isHandedOff ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => returnMutation.mutate(selected.id)}
                        disabled={returnMutation.isPending}
                        title="Вернуть диалог под управление ИИ"
                      >
                        {returnMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <RotateCcw className="h-4 w-4 mr-1" />
                        )}
                        Вернуть боту
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handoffMutation.mutate(selected.id)}
                        disabled={handoffMutation.isPending}
                        title="Забрать диалог себе — ИИ перестанет отвечать"
                      >
                        {handoffMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Headset className="h-4 w-4 mr-1" />
                        )}
                        Взять себе
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() =>
                        setConfirmDelete({
                          kind: "single",
                          id: selected.id,
                          title:
                            selected.user_name ||
                            `Гость ${selected.visitor_id.slice(0, 8)}`,
                        })
                      }
                      title="Удалить диалог"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <ScrollArea className="flex-1 min-h-0 p-4">
                <div className="space-y-4">
                  {selected.messages.map((message) => {
                    const isUser = message.role === "user";
                    const isSystem = message.role === "system";
                    const isManager = message.role === "manager";
                    return (
                      <div
                        key={message.id}
                        className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
                      >
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarFallback>
                            {isUser ? (
                              <User className="h-4 w-4" />
                            ) : isSystem ? (
                              <Wrench className="h-4 w-4" />
                            ) : isManager ? (
                              <Headset className="h-4 w-4" />
                            ) : (
                              <Bot className="h-4 w-4" />
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div
                          className={`max-w-[70%] ${
                            isUser ? "items-end" : "items-start"
                          }`}
                        >
                          <div
                            className={`p-3 rounded-lg whitespace-pre-line text-sm ${
                              isUser
                                ? "bg-primary text-primary-foreground"
                                : isSystem
                                  ? "bg-warning/15 text-warning-foreground"
                                  : isManager
                                    ? "bg-success/15 text-foreground"
                                    : "bg-muted"
                            }`}
                          >
                            {message.content}
                            {message.attachment && (
                              <div className="mt-2 pt-2 border-t border-current/20">
                                <a
                                  href={message.attachment.download_url}
                                  download
                                  className={`inline-flex items-center gap-1.5 text-xs font-medium underline ${
                                    isUser ? "text-primary-foreground" : "text-primary"
                                  }`}
                                >
                                  <Paperclip className="h-3.5 w-3.5" />
                                  Скачать «{message.attachment.filename}» ({formatBytes(message.attachment.size_bytes)})
                                </a>
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 flex gap-2">
                            <span>{formatTime(message.created_at)}</span>
                            {isManager && <span className="text-success">· менеджер</span>}
                            {message.model_used && (
                              <span>· {message.model_used}</span>
                            )}
                            {message.is_fallback && (
                              <span className="text-warning">· fallback</span>
                            )}
                            {message.latency_ms !== null && (
                              <span>· {(message.latency_ms / 1000).toFixed(1)}с</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>

              {/* Поле ответа менеджера — только когда диалог у менеджера */}
              {isHandedOff && (
                <div className="p-3 border-t flex items-end gap-2">
                  <input
                    ref={managerFileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      e.target.value = "";
                      if (!f) return;
                      if (f.size > 30 * 1024 * 1024) {
                        toast.error("Файл больше 30 МБ");
                        return;
                      }
                      managerFileMutation.mutate({ id: selected.id, file: f });
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => managerFileInputRef.current?.click()}
                    disabled={managerFileMutation.isPending}
                    title="Отправить файл клиенту"
                  >
                    {managerFileMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Paperclip className="h-4 w-4" />
                    )}
                  </Button>
                  <Textarea
                    rows={2}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Ответ клиенту от лица менеджера…"
                    className="resize-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && replyText.trim()) {
                        replyMutation.mutate({ id: selected.id, text: replyText.trim() });
                      }
                    }}
                  />
                  <Button
                    onClick={() =>
                      replyText.trim() &&
                      replyMutation.mutate({ id: selected.id, text: replyText.trim() })
                    }
                    disabled={replyMutation.isPending || !replyText.trim()}
                    title="Отправить (Ctrl+Enter)"
                  >
                    {replyMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Боковая панель */}
        <Card className="col-span-3 p-4 space-y-4 overflow-y-auto">
          <h3 className="font-medium">Информация</h3>

          {!selected ? (
            <p className="text-sm text-muted-foreground">Выбери диалог</p>
          ) : (
            <>
              {isHandedOff && (
                <>
                  <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
                    Диалог ведёт менеджер. ИИ-продавец не отвечает, пока диалог
                    не вернут боту.
                    {selected.handoff_reason && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Причина: {selected.handoff_reason}
                      </div>
                    )}
                  </div>
                  <Separator />
                </>
              )}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Выжимка для менеджера
                </h4>
                <p className="text-sm whitespace-pre-line">
                  {selected.summary ||
                    "Появляется при передаче диалога менеджеру."}
                </p>
              </div>
              <Separator />
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Контакт клиента
                </h4>
                <p className="text-sm">
                  {selected.user_contact || "Не предоставлены"}
                </p>
              </div>
              <Separator />
              {selected.attachments && selected.attachments.length > 0 && (
                <>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Paperclip className="h-3.5 w-3.5" />
                      Файлы клиента ({selected.attachments.length})
                    </h4>
                    <ul className="space-y-1.5">
                      {selected.attachments.map((att) => (
                        <li key={att.id} className="text-sm">
                          <a
                            href={att.download_url}
                            className="text-primary hover:underline break-all"
                            download
                          >
                            {att.filename}
                          </a>
                          <span className="text-muted-foreground text-xs ml-2">
                            {formatBytes(att.size_bytes)}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      Файлы — заказы клиента на расчёт. Нейросеть их не открывает.
                    </p>
                  </div>
                  <Separator />
                </>
              )}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Заявка по диалогу
                </h4>
                <p className="text-sm">
                  {selected.has_lead
                    ? "Да — открой раздел «Заявки»"
                    : "Не создана"}
                </p>
              </div>
              <Separator />
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Когда начат
                </h4>
                <p className="text-sm">{formatDateTime(selected.started_at)}</p>
              </div>
            </>
          )}
        </Card>
      </div>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.kind === "bulk"
                ? "Удалить отфильтрованные диалоги?"
                : "Удалить диалог?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.kind === "bulk" ? (
                <>
                  Будут удалены все диалоги, попадающие в текущий фильтр —
                  около <b>{confirmDelete.count}</b> штук. История переписки
                  сотрётся, но связанные заявки сохранятся в разделе «Заявки»
                  (просто без ссылки на диалог).
                </>
              ) : (
                <>
                  Будет удалена вся переписка с{" "}
                  <b>{confirmDelete?.kind === "single" ? confirmDelete.title : ""}</b>.
                  Заявка по этому диалогу (если есть) сохранится в разделе
                  «Заявки».
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending || bulkDeleteMutation.isPending}>
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending || bulkDeleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!confirmDelete) return;
                if (confirmDelete.kind === "single") {
                  deleteMutation.mutate(confirmDelete.id, {
                    onSettled: () => setConfirmDelete(null),
                  });
                } else {
                  bulkDeleteMutation.mutate(undefined, {
                    onSettled: () => setConfirmDelete(null),
                  });
                }
              }}
            >
              {(deleteMutation.isPending || bulkDeleteMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
